/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector } from '@cloudio-saas/statex';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { BGColorProp, constants } from 'componentregistry';
import { helpTextSelector } from 'designer/state';
import React, {
  memo,
  startTransition,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
} from 'react';
import { backgroundColorStyleFn, BGColor } from 'theme/useThemeColor';
import clickOnEnterOrSpace from 'utils/clickOnEnterOrSpace';
import { Box, Theme } from '@mui/material';
import Icon from 'icons/Icon';
import { lightenBorder, useTheme } from 'theme/Theme';
import { getDefaultValues } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';
import { ColorPickerPopover } from './ColorExpressionProperty';

const { backgroundColor } = constants;

const useOptionStyles = makeStyles((theme: Theme) => ({
  option: {
    backgroundColor: backgroundColorStyleFn(theme),
    alignSelf: 'center',
    borderRadius: 16,
    cursor: 'pointer',
    flex: '0 0 16px',
    height: 16,
    margin: '0 4px',
    outline: 'none',
    overflow: 'hidden',
    padding: 0,
    position: 'relative',
    width: 16,
  },
  transparent: {
    backgroundImage: `url('data:image/svg+xml,\
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="${
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.3)'
        : 'rgb(221,221,221)'
    }">\
    <rect x="20" width="20" height="20"/><rect y="20" width="20" height="20"/></svg>')`,
    backgroundSize: '8px 8px',
  },
  disabled: {
    pointerEvents: 'none',
    opacity: 0.4,
  },
}));

function Option({
  value,
  label,
  onSelect,
  disabled = false,
}: {
  value: BGColor;
  label: string;
  onSelect: (v: BGColor) => void;
  disabled: boolean;
}) {
  const classes = useOptionStyles({ backgroundColor: value });
  const onClick = () => onSelect(value);
  return (
    // eslint-disable-next-line jsx-a11y/control-has-associated-label
    <div
      onKeyDown={(e) => clickOnEnterOrSpace(e, onClick)}
      role="button"
      tabIndex={0}
      data-tip={label}
      onClick={onClick}
      className={clsx({
        [classes.option]: true,
        [classes.transparent]: value === 'transparent',
        [classes.disabled]: disabled,
      })}
    />
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 0 0',
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    position: 'relative',
    height: '24px',
  },
  label: {
    flex: 0,
    minWidth: 80,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  s: ({ color = 'paper' }: { color?: BGColor }) => ({
    backgroundColor: theme.palette.background.default,
    position: 'absolute',
    top: 0,
    bottom: 0,
    borderRadius: 24,
    border: `2px solid ${lightenBorder(
      color === 'default' || color === 'paper'
        ? theme.palette.background[color]
        : color,
      theme,
    )}`,
    boxShadow: 'rgb(0 0 0 / 25%) 0px 1px 6px 0px',
    transition: 'all 0.2s cubic-bezier(0.2,0,0,1)',
  }),
  tip: { marginBottom: 8, marginLeft: 8 },
}));

export function BGColorOptions({
  value,
  onSelect,
  allowCustomColor,
  isViewOnlyUser = false,
}: {
  value: BGColor;
  onSelect: (v: BGColor) => void;
  allowCustomColor: boolean;
  isViewOnlyUser?: boolean;
}) {
  const classes = useStyles({ color: value });
  const keys = Object.keys(backgroundColor) as (keyof typeof backgroundColor)[];
  let index = keys.indexOf(value as keyof typeof backgroundColor);
  let custom = false;
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  if (index === -1) {
    custom = true;
    index = 3;
  }

  return (
    <div ref={ref} className={classes.inner}>
      <Box
        sx={{
          width: '24px',
          left: `${24 * index}px`,
        }}
        className={classes.s}
      />
      {keys.map((key) => (
        <Option
          disabled={isViewOnlyUser}
          key={key}
          value={key}
          label={backgroundColor[key]}
          onSelect={onSelect}
        />
      ))}
      {allowCustomColor ? (
        <Option
          disabled={isViewOnlyUser}
          key="custom"
          value={
            custom
              ? value || theme.palette.primary.main
              : theme.palette.primary.main
          }
          label="Custom Color"
          onSelect={() => {
            setAnchorEl(ref.current);
          }}
        />
      ) : null}
      <ColorPickerPopover
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        onChange={onSelect}
        value={custom ? value : ''}
      />
    </div>
  );
}

function BGColorProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<BGColor>(mgr, property),
  onChange,
  isViewOnlyUser = false,
}: PropertyEditorProps<BGColorProp, BGColor>) {
  const classes = useStyles({ color: value });
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const [color, setColor] = useState(value);
  const val = useDeferredValue(color);
  useEffect(() => {
    if (val !== value) {
      startTransition(() => {
        onChange(property, val);
      });
    }
  }, [onChange, property, val, value]);

  return (
    <div data-testid={property} className={classes.root}>
      <div className={classes.label}>{def.label}</div>
      <BGColorOptions
        value={color}
        onSelect={setColor}
        allowCustomColor={def.allowCustomColor}
        isViewOnlyUser={isViewOnlyUser}
      />
      {helpText ? (
        <Icon
          className={classes.tip}
          data-tip-at="top"
          data-tip-delay={0}
          data-tip={helpText}
          iconName="comment-exclamation"
        />
      ) : null}
    </div>
  );
}

export default memo(BGColorProperty);
